import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  TextField,
  EmojiPicker,
  FileView,
  UploadBox,
} from '../../../components';
import { Icon } from '../../../components/Icon';
import { ContentHeader } from './ContentHeader';
import useToast from '../../../hooks/useToast';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import {
  ACCEPT_IMAGES,
  ACCEPT_PDF,
  ACCEPT_VIDEO,
  MAX_LINE_IMAGE_SIZE,
  MAX_LINE_VIDEO_SIZE,
  MAX_PDF_FILE_SIZE,
  messageStrings,
  ONE_MB,
  sendAllMessageStrings,
} from '../../../constants';
import { TAutoReplyMessageAsset } from 'api';
import { EContentSectionType, EMoveDirection, EUrlButtonType, TMessageSection } from 'types';
import { EOrganizationFeature } from '../../../types/enum';
import { isSectionFilled } from 'utils/messageSection';
import { isOrganizationFeatureEnabled } from '../../../utils/organizationFeature';
import { validateFile } from 'utils/file';
import { isValidUrl } from 'utils';
import { ContentSectionVariant } from './ContentHeader';
import { SurveySectionSelector } from './SurveySectionSelector';
import { AssetSectionSelector } from './AssetSectionSelector';

type Props = {
  section: TMessageSection;
  index: number;
  totalSections: number;
  onChangeType: (type: EContentSectionType) => void;
  onUpdateSection: (updates: Partial<TMessageSection>) => void;
  onChangeContent: (content: string) => void;
  onChangeFile: (file: File) => void;
  onRemoveFile: () => void;
  onDelete: () => void;
  onMove: (direction: EMoveDirection) => void;
  hideActionUrl?: boolean;
  variant?: ContentSectionVariant;
  /** 一斉送信の名前差し込み説明は、最初のテキスト欄だけに表示する */
  showNameInsertionHints?: boolean;
  assets?: TAutoReplyMessageAsset[];
  /** PDF(FILE)セクションを選べるようにするか（挨拶 + 個別チャットにも送信ON のときのみ） */
  allowFileSection?: boolean;
  /** 個別チャットへも送る挨拶など、用途に応じた動画上限 */
  maxVideoBytes?: number;
  /** アンケート招待画像のアップロード進行状態を親フォームへ伝える */
  onSurveyUploadingChange?: (uploading: boolean) => void;
};

const contentSectionClasses = {
  card: 'relative overflow-hidden rounded-xl border border-slate-200 bg-white text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md',
  textAreaWrap: 'flex flex-col gap-2 bg-[#F9FAFB]',
  uploaderWrap: 'flex flex-col gap-2',
  filePreview: 'flex h-36 gap-4 overflow-x-auto bg-[#F9FAFB] p-2',
  pdfFileRow: 'flex items-center gap-2 bg-[#F9FAFB] p-4',
  pdfFileName: 'min-w-0 flex-1 truncate text-sm text-neutral-600',
  pdfRemoveButton:
    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-200 transition hover:bg-neutral-300',
  emojiButton: 'flex items-center gap-2 px-4 py-2 text-sm text-neutral-300',
  textFieldClass: 'w-full bg-[#F9FAFB] rounded-none',
  textFieldInputClass: 'resize-none',
} as const;

function ContentSection({
  section,
  index,
  totalSections,
  onChangeType,
  onUpdateSection,
  onChangeContent,
  onChangeFile,
  onRemoveFile,
  onDelete,
  onMove,
  hideActionUrl = false,
  variant = 'bulk',
  showNameInsertionHints = true,
  assets = [],
  allowFileSection = false,
  maxVideoBytes = MAX_LINE_VIDEO_SIZE,
  onSurveyUploadingChange,
}: Props) {
  const { toastError } = useToast();
  const { data: currentUser } = useCurrentUser();
  const allowSurveySection = isOrganizationFeatureEnabled(
    currentUser,
    EOrganizationFeature.SURVEY,
  );
  const hasContentFlag = isSectionFilled(section);
  const [urlError, setUrlError] = useState<{ actionUrl?: boolean }>({});
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const hasFocusedTextRef = useRef(false);
  const [pendingNameInsertion, setPendingNameInsertion] = useState<{
    content: string;
    position: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!pendingNameInsertion) return;
    const textarea = textInputRef.current;
    if (
      textarea &&
      section.type === EContentSectionType.TEXT &&
      section.content === pendingNameInsertion.content
    ) {
      textarea.focus();
      textarea.setSelectionRange(
        pendingNameInsertion.position,
        pendingNameInsertion.position,
      );
    }
    setPendingNameInsertion(null);
  }, [pendingNameInsertion, section]);

  const handleInsertName = () => {
    if (variant !== 'bulk' || section.type !== EContentSectionType.TEXT) return;
    const textarea = textInputRef.current;
    const start = hasFocusedTextRef.current && textarea
      ? textarea.selectionStart
      : section.content.length;
    const end = hasFocusedTextRef.current && textarea
      ? textarea.selectionEnd
      : section.content.length;
    const token = '{{name}}';
    const content = section.content.slice(0, start) + token + section.content.slice(end);
    onChangeContent(content);
    setPendingNameInsertion({ content, position: start + token.length });
  };

  const handleTypeSelect = useCallback(
    (type: EContentSectionType | EUrlButtonType) => {
      // Handle URL button types
      if (type === EUrlButtonType.IMAGE_URL && section.type === EContentSectionType.IMAGE) {
        const currentShowUrlInput = 'showUrlInput' in section ? section.showUrlInput : false;
        if (currentShowUrlInput) {
          onUpdateSection({ showUrlInput: false, actionUrl: '' });
        } else {
          onUpdateSection({ showUrlInput: true });
        }
        return;
      }

      if (type === EUrlButtonType.VIDEO_URL && section.type === EContentSectionType.VIDEO) {
        const currentShowUrlInput = 'showUrlInput' in section ? section.showUrlInput : false;
        if (currentShowUrlInput) {
          onUpdateSection({ showUrlInput: false, actionUrl: '', actionLabel: '' });
        } else {
          const defaultActionLabel = sendAllMessageStrings.actionLabelDefault;
          onUpdateSection({
            showUrlInput: true,
            actionLabel: 'actionLabel' in section ? (section.actionLabel || defaultActionLabel) : defaultActionLabel
          });
        }
        return;
      }

      // Handle content section types
      const isBulkType =
        type === EContentSectionType.TEXT ||
        type === EContentSectionType.IMAGE ||
        type === EContentSectionType.VIDEO ||
        type === EContentSectionType.RICH_MESSAGE ||
        type === EContentSectionType.CARD_TYPE_MESSAGE ||
        type === EContentSectionType.SURVEY;
      const isMessageSettingsType =
        type === EContentSectionType.TEXT ||
        type === EContentSectionType.IMAGE ||
        type === EContentSectionType.VIDEO;
      const isGreetingMessageSettingsType =
        isMessageSettingsType ||
        type === EContentSectionType.CARD_TYPE_MESSAGE ||
        type === EContentSectionType.SURVEY ||
        (type === EContentSectionType.FILE && allowFileSection);
      // 自動応答で選べる種別は一斉送信と同一。二重管理して片方に追加し忘れると
      // 「ボタンは出るが押しても切り替わらない」状態になるため、isBulkType を再利用する
      const isAutoReplyType = isBulkType;

      if (
        (variant === 'bulk' && isBulkType) ||
        (variant === 'messageSettings' && isMessageSettingsType) ||
        (variant === 'greetingMessageSettings' &&
          isGreetingMessageSettingsType) ||
        (variant === 'autoReply' && isAutoReplyType)
      ) {
        if (!section.type || !hasContentFlag) {
          onChangeType(type);
        }
      }
    },
    [
      allowFileSection,
      hasContentFlag,
      onChangeType,
      onUpdateSection,
      section,
      variant,
    ],
  );

  const handleSelectImage = useCallback(
    (file: File) => {
      const isValid = validateFile({
        file,
        acceptedTypes: ACCEPT_IMAGES,
        maxBytes: MAX_LINE_IMAGE_SIZE,
        toastError,
      });
      if (isValid) onChangeFile(file);
    },
    [onChangeFile, toastError],
  );

  const handleSelectVideo = useCallback(
    (file: File) => {
      const isValid = validateFile({
        file,
        acceptedTypes: ACCEPT_VIDEO,
        maxBytes: maxVideoBytes,
        toastError,
        sizeErrorMessage: messageStrings.videoLimitSize,
      });
      if (isValid) onChangeFile(file);
    },
    [maxVideoBytes, onChangeFile, toastError],
  );

  const handleSelectPdf = useCallback(
    (file: File) => {
      const isValid = validateFile({
        file,
        acceptedTypes: ACCEPT_PDF,
        maxBytes: MAX_PDF_FILE_SIZE,
        toastError,
        sizeErrorMessage: messageStrings.pdfLimitSize,
      });
      if (isValid) onChangeFile(file);
    },
    [onChangeFile, toastError],
  );

  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      const isActionUrl = e.target.name.includes('actionUrl');

      if (isActionUrl) {
        const isValid = isValidUrl(value);
        setUrlError((prev) => ({
          ...prev,
          actionUrl: value.trim() ? !isValid : false,
        }));
        onUpdateSection({ actionUrl: value });
      } else {
        onUpdateSection({ content: value });
      }
    },
    [onUpdateSection]
  )

  const handleActionLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onUpdateSection({ actionLabel: e.target.value });
    },
    [onUpdateSection]
  );


  const renderContentInput = () => {
    if (!section.type) return null;

    switch (section.type) {
      case EContentSectionType.TEXT:
        return (
          <div className={contentSectionClasses.textAreaWrap}>
            <TextField
              type="textarea"
              name={`section-${section.id}`}
              value={section.content}
              onChange={handleContentChange}
              placeholder={messageStrings.inputPlaceholder}
              className={contentSectionClasses.textFieldClass}
              inputClass={contentSectionClasses.textFieldInputClass}
              rows={4}
              noBorder
              inputRef={textInputRef}
              onFocus={() => { hasFocusedTextRef.current = true; }}
            />
            {variant === 'bulk' && showNameInsertionHints && (
              <div className="px-4 text-xs text-neutral-500">
                <p>{sendAllMessageStrings.nameInsertion.hint}</p>
                <p>{sendAllMessageStrings.nameInsertion.namePriority}</p>
                <p>{sendAllMessageStrings.nameInsertion.lengthHint}</p>
              </div>
            )}
            <div className="flex items-center">
              <EmojiPicker
                onEmojiSelect={(emoji) => {
                  onChangeContent(section.content + emoji.native);
                }}
                modal
              >
                <button
                  type="button"
                  className={contentSectionClasses.emojiButton}
                >
                  <Icon icon="emoji" width={16} height={16} />
                  {sendAllMessageStrings.emoji}
                </button>
              </EmojiPicker>
              {variant === 'bulk' && (
                <button
                  type="button"
                  className="px-4 py-2 text-sm text-primary hover:text-primary-600"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleInsertName}
                >
                  {sendAllMessageStrings.nameInsertion.button}
                </button>
              )}
            </div>
          </div>
        );

      case EContentSectionType.IMAGE:
        return (
          <div className={contentSectionClasses.uploaderWrap}>
            {section.file || section.originalMedia?.url ? (
              <div className={contentSectionClasses.filePreview}>
                <FileView
                  className="h-full"
                  file={section.file || { url: section.originalMedia?.url!, type: 'image/png' }}
                  onRemove={section.file ? onRemoveFile : undefined}
                />
              </div>
            ) : (
              <UploadBox
                title={sendAllMessageStrings.uploadPhoto}
                icon="image"
                acceptTypes={ACCEPT_IMAGES}
                maxSizeMb={MAX_LINE_IMAGE_SIZE / ONE_MB}
                onSelect={handleSelectImage}
              />
            )}

            {!hideActionUrl && section.showUrlInput && (
              <div className="p-4 bg-[#F9FAFB]">
                <TextField
                  type="url"
                  name={`actionUrl-${section.id}`}
                  value={section.actionUrl || ''}
                  onChange={handleContentChange}
                  placeholder={sendAllMessageStrings.placeholders.actionUrlInput}
                  className="w-full bg-white"
                  error={urlError.actionUrl}
                />
                {urlError.actionUrl && (
                  <p className="text-red-500 text-sm mt-1">{sendAllMessageStrings.errors.invalidUrl}</p>
                )}
              </div>
            )}
          </div>
        );

      case EContentSectionType.VIDEO:
        return (
          <div className={contentSectionClasses.uploaderWrap}>
            {section.file || section.originalMedia?.url ? (
              <div className={contentSectionClasses.filePreview}>
                <FileView
                  className="h-full"
                  file={section.file || { url: section.originalMedia?.url!, type: 'video' }}
                  onRemove={section.file ? onRemoveFile : undefined}
                />
              </div>
            ) : (
              <UploadBox
                title={sendAllMessageStrings.uploadVideo}
                icon="attach"
                acceptTypes={ACCEPT_VIDEO}
                maxSizeMb={maxVideoBytes / ONE_MB}
                onSelect={handleSelectVideo}
              />
            )}

            {!hideActionUrl && section.showUrlInput && (
              <div className="p-4 bg-[#F9FAFB] flex flex-col gap-3">
                <div>
                  <TextField
                    type="url"
                    name={`actionUrl-${section.id}`}
                    value={section.actionUrl || ''}
                    onChange={handleContentChange}
                    placeholder={sendAllMessageStrings.placeholders.actionUrlInput}
                    className="w-full bg-white"
                    error={urlError.actionUrl}
                  />
                  {urlError.actionUrl && (
                    <p className="text-red-500 text-sm mt-1">{sendAllMessageStrings.errors.invalidUrl}</p>
                  )}
                </div>
                <TextField
                  type="text"
                  name={`actionLabel-${section.id}`}
                  value={section.actionLabel || ''}
                  onChange={handleActionLabelChange}
                  placeholder={sendAllMessageStrings.actionLabelDefault}
                  className="w-full bg-white"
                />
              </div>
            )}
          </div>
        );

      case EContentSectionType.FILE:
        return (
          <div className={contentSectionClasses.uploaderWrap}>
            {section.file || section.originalMedia?.url ? (
              <div className={contentSectionClasses.pdfFileRow}>
                <Icon icon="attach" width={20} height={20} color="#9CA3AF" />
                <span className={contentSectionClasses.pdfFileName}>
                  {section.file?.name || section.originalMedia?.fileName}
                </span>
                {section.file && (
                  <button
                    type="button"
                    onClick={onRemoveFile}
                    className={contentSectionClasses.pdfRemoveButton}
                    title={sendAllMessageStrings.contentHeader.delete}
                  >
                    <Icon icon="close" width={16} height={16} />
                  </button>
                )}
              </div>
            ) : (
              <UploadBox
                title={sendAllMessageStrings.uploadPdf}
                icon="attach"
                acceptTypes={ACCEPT_PDF}
                maxSizeMb={MAX_PDF_FILE_SIZE / ONE_MB}
                onSelect={handleSelectPdf}
              />
            )}
          </div>
        );

      case EContentSectionType.RICH_MESSAGE:
      case EContentSectionType.CARD_TYPE_MESSAGE:
        return (
          <AssetSectionSelector
            section={section}
            assets={assets}
            onUpdateSection={onUpdateSection}
          />
        );

      case EContentSectionType.SURVEY:
        return (
          <SurveySectionSelector
            section={section}
            onUpdateSection={onUpdateSection}
            onUploadingChange={onSurveyUploadingChange}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={contentSectionClasses.card}>
      <ContentHeader
        section={section}
        index={index}
        totalSections={totalSections}
        onMove={onMove}
        onDelete={onDelete}
        onTypeSelect={handleTypeSelect}
        hasContentFlag={hasContentFlag}
        hideActionUrl={hideActionUrl}
        variant={variant}
        allowFileSection={allowFileSection}
        allowSurveySection={allowSurveySection}
      />
      {renderContentInput()}
    </div>
  );
}

export default ContentSection;
